/**
 * 已发布模型页的四个弹窗：复制模型、可见性与标签（白名单、图标）、按采购价重定价、发布上游模型。
 *
 * 背景：对应 Alice 模型页的「复制」「设置白名单用户」「按采购价重定价」「发布模型」。写操作都只有 admin 能做（后端校验），
 *   保存后生成新配置版本。
 * 设计意图：
 *   - 重定价默认先预览（dry_run），预览里逐个模型列出采购价和跳过原因，确认后才真正写库。
 *   - 发布时没有采购价的模型由后端建成停用并标 needs_pricing，结果里原样告诉运营，不替运营定价。
 *   - 对外 ID 的 slug 规则与后端 _slug 一致，前端只做预填，最终由后端校验。
 * 关键约束：弹窗里有输入框，一律不允许点遮罩关闭（Modal 默认行为）；错误显示在弹窗里，不关闭。
 */
import { useEffect, useMemo, useState } from 'react'
import { catalogApi, type CatalogModel, type LibraryItem, type PublishResult, type RepriceRow } from '@ap/api-client/admin-catalog'
import { A } from '@ap/i18n/admin'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Button, Field, Form, Input, Modal, Notice, Switch, Textarea, useToast } from '@ap/ui'

/** 与后端 catalog_models._slug 同规则：非法字符换成连字符，首字符必须是字母或数字。 */
export function slugOf(value: string): string {
  return value
    .replace(/[^A-Za-z0-9._:/-]+/g, '-')
    .replace(/^[-/._:]+|[-/._:]+$/g, '')
    .slice(0, 120)
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** 复制模型：新对外 ID 与显示名；路由一并复制，新模型默认停用。 */
export function CopyModal({ model, onClose, onSaved }: { model: CatalogModel | null; onClose: () => void; onSaved: (id: string) => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<CatalogModel | null>(null)
  if (model !== shown) {
    setShown(model)
    setError('')
    if (model) {
      setId(`${model.id}-copy`)
      setName(model.display_name ? `${model.display_name} (copy)` : '')
    }
  }
  const submit = async () => {
    if (!model) return
    setBusy(true)
    setError('')
    try {
      const r = await catalogApi.copyModel(model.id, id.trim(), name.trim())
      toast.ok(t(G.models.copied, { id: r.id, v: r.config_version }))
      onSaved(r.id)
    } catch (e) {
      console.error('[models] 复制失败', e)
      setError(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal open={model !== null} onClose={onClose} busy={busy} title={t(G.models.copyTitle, { id: model?.id ?? '' })}>
      <Form onSubmit={submit}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(G.models.copyLead)}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        <Field label={t(A.models.id)} hint={t(A.models.idHint)}>
          <Input mono required pattern="[A-Za-z0-9][A-Za-z0-9._:/\-]{0,119}" value={id} onChange={(e) => setId(e.target.value)} />
        </Field>
        <Field label={t(A.models.displayName)}>
          <Input maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(G.models.copy)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

/** 可见性与展示：公开开关、白名单账号、标签、图标。public 为假时只有白名单账号在 /v1/models 里看得到（调用权限不受影响）。 */
export function VisibilityModal({ model, onClose, onSaved }: { model: CatalogModel | null; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [pub, setPub] = useState(true)
  const [accounts, setOrgs] = useState('')
  const [tags, setTags] = useState('')
  const [icon, setIcon] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<CatalogModel | null>(null)
  if (model !== shown) {
    setShown(model)
    setError('')
    if (model) {
      setPub(model.public)
      setOrgs(model.allowed_accounts.join('\n'))
      setTags(model.tags.join(', '))
      setIcon(model.icon_url)
    }
  }
  const submit = async () => {
    if (!model) return
    setBusy(true)
    setError('')
    try {
      const list = accounts
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      const bad = list.find((s) => !UUID_RE.test(s))
      if (bad) throw new Error(t(G.models.badOrg, { v: bad }))
      const r = await catalogApi.patchModel(model.id, {
        public: pub,
        allowed_accounts: list,
        tags: tags
          .split(/[,，]/)
          .map((s) => s.trim())
          .filter(Boolean),
        icon_url: icon.trim(),
      })
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      onSaved()
    } catch (e) {
      console.error('[models] 保存可见性失败', e)
      setError(e instanceof Error && !('code' in e) ? e.message : errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal open={model !== null} onClose={onClose} busy={busy} title={t(G.models.visTitle, { id: model?.id ?? '' })}>
      <Form onSubmit={submit}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(G.models.visLead)}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        <Switch checked={pub} onChange={setPub} label={t(G.models.visPublic)} />
        <Field label={t(G.models.visOrgs)} hint={t(G.models.visOrgsHint)}>
          <Textarea rows={4} spellCheck={false} className="ap-mono" value={accounts} onChange={(e) => setOrgs(e.target.value)} />
        </Field>
        <Field label={t(G.models.visTags)} hint={t(G.models.visTagsHint)}>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} />
        </Field>
        <Field label={t(G.models.visIcon)}>
          <Input mono placeholder="https://..." maxLength={500} value={icon} onChange={(e) => setIcon(e.target.value)} />
        </Field>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

/** 按采购价重定价：卖价 = 主路由采购价 × 倍率。先预览再应用。 */
export function RepriceModal({
  open,
  modelIds,
  onClose,
  onApplied,
}: {
  open: boolean
  modelIds: string[]
  onClose: () => void
  onApplied: () => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [markup, setMarkup] = useState('1.3')
  const [plan, setPlan] = useState<RepriceRow[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setPlan(null)
      setError('')
    }
  }
  const run = async (dry: boolean) => {
    setBusy(true)
    setError('')
    try {
      const r = await catalogApi.reprice(modelIds, markup.trim(), dry)
      setPlan(r.plan)
      if (!dry) {
        toast.ok(t(G.models.repriceDone, { n: r.applied ?? 0, v: r.config_version ?? '-' }))
        onApplied()
      }
    } catch (e) {
      console.error('[models] 重定价失败', e)
      setError(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }
  const ready = plan?.filter((p) => p.status === 'ready').length ?? 0
  return (
    <Modal open={open} onClose={onClose} busy={busy} wide title={t(G.models.repriceTitle)}>
      <Form onSubmit={() => run(true)}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(G.models.repriceLead, { n: modelIds.length })}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        <Field label={t(G.models.repriceMarkup)} hint={t(G.models.repriceMarkupHint)}>
          <Input inputMode="decimal" required pattern="[0-9]+(\.[0-9]{1,4})?" value={markup} onChange={(e) => { setMarkup(e.target.value); setPlan(null) }} />
        </Field>
        {plan && (
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border-medium)', borderRadius: 6 }}>
            <table>
              <thead>
                <tr>
                  <th>{t(A.models.id)}</th>
                  <th>{t(G.models.repriceCost)}</th>
                  <th>{t(G.models.repriceResult)}</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p) => (
                  <tr key={p.model_id}>
                    <td className="mono">{p.model_id}</td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {p.cost_usd ? Object.entries(p.cost_usd).map(([k, v]) => `${k} $${v}`).join(' / ') : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {p.status === 'skipped' ? (
                        <span className="badge badge-info">{t(G.models.repriceSkip[p.reason ?? 'unknown'] ?? G.models.repriceSkip.unknown)}</span>
                      ) : p.status === 'applied' ? (
                        <span className="badge badge-ok">{t(G.models.repriceApplied)}</span>
                      ) : (
                        <span className="badge badge-ok">{t(G.models.repriceReady)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" loading={busy && plan === null}>
            {t(G.models.repriceDry)}
          </Button>
          <Button variant="primary" disabled={plan === null || ready === 0} loading={busy && plan !== null} onClick={() => run(false)}>
            {t(G.models.repriceApply, { n: ready })}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

/** 发布模型：把渠道模型库里还没被任何对外模型引用的上游模型批量发布成对外模型。 */
export function PublishModal({ open, onClose, onPublished }: { open: boolean; onClose: () => void; onPublished: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [items, setItems] = useState<LibraryItem[] | null>(null)
  const [error, setError] = useState('')
  const [picked, setPicked] = useState<Record<string, boolean>>({})
  const [ids, setIds] = useState<Record<string, string>>({})
  const [markup, setMarkup] = useState('1.3')
  const [filter, setFilter] = useState('')
  const [results, setResults] = useState<PublishResult[] | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    let alive = true
    setItems(null)
    setError('')
    setPicked({})
    setIds({})
    setResults(null)
    catalogApi
      .libraryOverview(30)
      .then((r) => alive && setItems(r.data.filter((m) => m.enabled && m.route_refs === 0)))
      .catch((e) => {
        console.error('[models] 读取渠道模型库失败', e)
        if (alive) setError(errorTextCat(e, locale))
      })
    return () => {
      alive = false
    }
  }, [open, locale])

  const keyOf = (m: LibraryItem) => `${m.channel_id}\u0000${m.upstream_model}`
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return (items ?? []).filter((m) => !q || m.upstream_model.toLowerCase().includes(q) || m.channel_id.toLowerCase().includes(q))
  }, [items, filter])
  const chosen = (items ?? []).filter((m) => picked[keyOf(m)])

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await catalogApi.publishModels(
        chosen.map((m) => ({
          channel_id: m.channel_id,
          upstream_model: m.upstream_model,
          model_id: (ids[keyOf(m)] || slugOf(m.upstream_model)).trim(),
          markup: markup.trim() || '1',
        })),
      )
      setResults(r.results)
      toast.ok(t(G.models.publishDone, { n: r.created }))
      if (r.created) onPublished()
    } catch (e) {
      console.error('[models] 发布失败', e)
      setError(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} busy={busy} wide title={t(G.models.publishTitle)}>
      <div className="ap-stack" style={{ gap: 12 }}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(G.models.publishLead)}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        {results ? (
          <>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-medium)', borderRadius: 6 }}>
              <table>
                <thead>
                  <tr>
                    <th>{t(A.models.id)}</th>
                    <th>{t(G.models.publishUpstream)}</th>
                    <th>{t(G.models.repriceResult)}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={`${r.channel_id}/${r.upstream_model}`}>
                      <td className="mono">{r.model_id}</td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {r.channel_id}/{r.upstream_model}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.status === 'created' ? (
                          <span className={`badge ${r.needs_pricing ? 'badge-info' : 'badge-ok'}`}>{t(r.needs_pricing ? G.models.publishNeedsPricing : G.models.publishCreated)}</span>
                        ) : r.status === 'exists' ? (
                          <span className="badge badge-info">{t(G.models.publishExists)}</span>
                        ) : (
                          <span className="badge badge-off">{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
              <Button variant="primary" onClick={onClose}>
                {t(C.close)}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="ap-grid2">
              <Field label={t(G.models.publishFilter)}>
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} />
              </Field>
              <Field label={t(G.models.repriceMarkup)} hint={t(G.models.publishMarkupHint)}>
                <Input inputMode="decimal" pattern="[0-9]+(\.[0-9]{1,4})?" value={markup} onChange={(e) => setMarkup(e.target.value)} />
              </Field>
            </div>
            <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border-medium)', borderRadius: 6 }}>
              {items === null && !error ? (
                <div className="empty" style={{ padding: 24, textAlign: 'center' }}>
                  {t(G.common.loading)}
                </div>
              ) : shown.length === 0 ? (
                <div className="empty" style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  {t(G.models.publishEmpty)}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }} />
                      <th>{t(G.models.publishUpstream)}</th>
                      <th>{t(A.models.kind)}</th>
                      <th>{t(A.models.id)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((m) => {
                      const k = keyOf(m)
                      return (
                        <tr key={k}>
                          <td>
                            <input type="checkbox" checked={!!picked[k]} onChange={(e) => setPicked((x) => ({ ...x, [k]: e.target.checked }))} />
                          </td>
                          <td>
                            <div className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
                              {m.upstream_model}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                              {m.channel_name || m.channel_id}
                              {m.cost_missing ? ` · ${t(G.models.publishNoCost)}` : ''}
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-info">{m.kind}</span>
                          </td>
                          <td>
                            <input
                              className="form-input mono"
                              style={{ width: 200, padding: '5px 8px', fontSize: 12 }}
                              value={ids[k] ?? slugOf(m.upstream_model)}
                              onChange={(e) => setIds((x) => ({ ...x, [k]: e.target.value }))}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="ap-row" style={{ justifyContent: 'space-between' }}>
              <span className="ap-small ap-muted">{t(G.models.publishPicked, { n: chosen.length })}</span>
              <div className="ap-row">
                <Button onClick={onClose} disabled={busy}>
                  {t(C.cancel)}
                </Button>
                <Button variant="primary" disabled={chosen.length === 0} loading={busy} onClick={submit}>
                  {t(G.models.publishSubmit, { n: chosen.length })}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
